import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { CreateOfferView } from '../components/gigs/my-gigs/offers/CreateOfferView';
import { useOffer } from '../api/offers';
import { ProfileGuard } from '../components/auth/ProfileGuard';
import { FiAlertCircle } from 'react-icons/fi';
import { Button } from '../components/ui/Button';
import { toast } from '../utils/toast';
import { useUserStore } from '../store/useUserStore';
import { isOfferEffectivelyExpired, type OfferItem } from '../components/gigs/my-gigs/types';

export const Route = createFileRoute('/offers/$offerId/edit')({
  component: EditOfferPage,
});

const EDITABLE_STATUSES = new Set(['draft', 'changes_requested']);

function EditOfferPage() {
  const { offerId } = Route.useParams();
  const navigate = useNavigate();
  const { data: liveOffer, isLoading, isError } = useOffer(offerId);
  const currentUserId = useUserStore((s) => (s.user?.id != null ? String(s.user.id) : ''));

  // Gate on the offer as first loaded. Later refetches are triggered by this
  // page's own save (status flips to pending) and must not redirect the creator
  // or unmount the form while the revised offer is still being sent.
  const [gateOffer, setGateOffer] = useState<OfferItem | null>(null);
  useEffect(() => {
    if (liveOffer && currentUserId && !gateOffer) setGateOffer(liveOffer);
  }, [liveOffer, currentUserId, gateOffer]);
  const offer = gateOffer ?? liveOffer;

  const isCreator =
    !!offer && !!currentUserId && !!offer.creatorId && String(offer.creatorId) === currentUserId;
  const canEditHere = Boolean(offer && EDITABLE_STATUSES.has(offer.status));

  useEffect(() => {
    if (!gateOffer) return;
    const offer = gateOffer;
    if (!isCreator) {
      toast.error('Only the creator can edit this offer.');
      navigate({ to: '/offers/$offerId', params: { offerId }, replace: true });
      return;
    }
    if (offer.status === 'rejected' || isOfferEffectivelyExpired(offer)) {
      navigate({
        to: '/offers/create',
        search: {
          predecessorOfferUuid: offer.id,
          recipientEmail: offer.recipientEmail || undefined,
          recipientName: offer.recipientName || undefined,
          recipientUserId: offer.recipientUserId ?? undefined,
          conversationId: offer.conversationId || undefined,
          title: offer.title,
          description: offer.description,
          gigUuid: offer.gigUuid || undefined,
        },
        replace: true,
      });
      return;
    }
    if (canEditHere) return;
    if (offer.status === 'pending') {
      toast.error('This offer has been sent and can no longer be edited.');
    } else if (offer.status === 'accepted') {
      toast.error('This offer has already been accepted and can no longer be edited.');
    } else {
      toast.error('This offer can no longer be modified.');
    }
    navigate({ to: '/offers/$offerId', params: { offerId }, replace: true });
  }, [gateOffer, canEditHere, isCreator, currentUserId, navigate, offerId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white pb-28">
        <Header />
        <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)] flex flex-col items-center justify-center min-h-[50vh] text-center">
          <p className="text-sm text-zinc-400">Loading offer…</p>
        </main>
      </div>
    );
  }

  if (isError || !offer) {
    return (
      <div className="min-h-screen bg-black text-white pb-28">
        <Header />
        <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)] flex flex-col items-center justify-center min-h-[50vh] text-center">
          <FiAlertCircle size={48} className="text-zinc-600 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Offer not found</h2>
          <p className="text-sm text-zinc-400 mb-6">
            The requested offer could not be found to edit.
          </p>
          <Button
            onClick={() => navigate({ to: '/profile/my-gigs', search: { tab: 'offers' } })}
            size="lg"
            className="rounded-full px-8"
          >
            Back to My Gigs
          </Button>
        </main>
      </div>
    );
  }

  if (!canEditHere || (currentUserId && !isCreator)) {
    return (
      <div className="min-h-screen bg-black text-white pb-28">
        <Header />
        <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)]">
          <p className="text-sm text-zinc-400">Redirecting…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-28">
      <Header />
      <ProfileGuard>
        <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)]">
          <CreateOfferView
            initialOffer={offer}
            isEdit={true}
            onBack={() => navigate({ to: '/offers/$offerId', params: { offerId } })}
            onSubmitSuccess={(result) => {
              if (result && result.status !== 'draft' && result.conversationId) {
                navigate({
                  to: '/messages',
                  search: {
                    conversationUuid: result.conversationId,
                    offerUuid: result.offerId,
                  },
                });
                return;
              }
              navigate({ to: '/offers/$offerId', params: { offerId } });
            }}
          />
        </main>
      </ProfileGuard>
    </div>
  );
}
